import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import { isValidId } from "../../../../u2000Core/tools/U2000IdUtils";
import { NS, U3148ROLES } from "../../../U3148";
import type { U3148Organization } from "../../../U3148Service";
import { useU3148FindCountriesHandlers, useU3148FindStatesHandlers } from "./U3148UseFindGeoHandlers";
import U3148OrganizationIconPanel from "./U3148OrganizationIconPanel";

interface U3148OrganizationComponentProps<T extends Record<string, unknown>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

function U3148OrganizationBody<T extends U3148Organization & Record<string, unknown>>({ formStateHandlers, t }: U3148OrganizationComponentProps<T>) {
  const { hasRole } = useU2000AuthContext();
  const { i18n } = useTranslation();
  const canEditIcon = hasRole(U3148ROLES.EDIT);
  const findCountriesHandlers = useU3148FindCountriesHandlers();
  const findStatesHandlers = useU3148FindStatesHandlers();

  const setStateSelection = useRef<((item: unknown) => void) | null>(null);
  const setStateInputValue = useRef<((value: string) => void) | null>(null);
  const previousCountryIdRef = useRef<string | null>(null);

  const countryResultFields = useMemo(() => {
    if (i18n.language === "fr") return ["countryNameFr"];
    if (i18n.language === "en") return ["countryNameEn"];
    return ["countryName"];
  }, [i18n.language]);

  const stateResultFields = useMemo(() => {
    if (i18n.language === "fr") return ["stateNameFr"];
    if (i18n.language === "en") return ["stateNameEn"];
    return ["stateName"];
  }, [i18n.language]);

  const onSearchCountry = async (criterias: { fieldName: string; value: unknown }[]) => {
    return findCountriesHandlers.search(criterias);
  };

  const displayCountry = (item: Record<string, unknown> | null) => {
    if (!item) return "";
    if (i18n.language === "fr" && item.countryNameFr) return String(item.countryNameFr);
    if (i18n.language === "en" && item.countryNameEn) return String(item.countryNameEn);
    return String(item.countryName ?? item.name ?? "");
  };

  const extractCountryData = (result: Record<string, unknown> | null) => {
    if (result) {
      let countryName = "";
      if (i18n.language === "fr" && result.countryNameFr) countryName = String(result.countryNameFr);
      else if (i18n.language === "en" && result.countryNameEn) countryName = String(result.countryNameEn);
      else countryName = String(result.countryName ?? result.name ?? "");
      return [
        { fieldName: "country.id", value: result.id },
        { fieldName: "country.name", value: countryName },
        { fieldName: "country.countryName", value: countryName },
        { fieldName: "country.countryNameFr", value: result.countryNameFr ?? "" },
        { fieldName: "country.countryNameEn", value: result.countryNameEn ?? "" },
        { fieldName: "country.countryCode", value: result.countryCode ?? "" },
      ];
    }
    return [
      { fieldName: "country.id", value: null },
      { fieldName: "country.name", value: null },
      { fieldName: "country.countryName", value: null },
      { fieldName: "country.countryNameFr", value: null },
      { fieldName: "country.countryNameEn", value: null },
      { fieldName: "country.countryCode", value: null },
    ];
  };

  const onSearchState = async (criterias: { fieldName: string; value: unknown }[]) => {
    const data = formStateHandlers.getData() as U3148Organization | null;
    const finalCriterias = [...criterias];
    if (data?.country && isValidId(data.country.id)) {
      finalCriterias.push({ fieldName: "countryId", value: data.country.id.toString() });
    }
    return findStatesHandlers.search(finalCriterias);
  };

  const displayState = (item: Record<string, unknown> | null) => {
    if (!item) return "";
    if (i18n.language === "fr" && item.stateNameFr) return String(item.stateNameFr);
    if (i18n.language === "en" && item.stateNameEn) return String(item.stateNameEn);
    return String(item.stateName ?? item.name ?? "");
  };

  const extractStateData = (result: Record<string, unknown> | null) => {
    if (result) {
      let stateName = "";
      if (i18n.language === "fr" && result.stateNameFr) stateName = String(result.stateNameFr);
      else if (i18n.language === "en" && result.stateNameEn) stateName = String(result.stateNameEn);
      else stateName = String(result.stateName ?? result.name ?? "");
      return [
        { fieldName: "state.id", value: result.id },
        { fieldName: "state.name", value: stateName },
        { fieldName: "state.stateName", value: stateName },
        { fieldName: "state.stateNameFr", value: result.stateNameFr ?? "" },
        { fieldName: "state.stateNameEn", value: result.stateNameEn ?? "" },
        { fieldName: "state.stateCode", value: result.stateCode ?? "" },
      ];
    }
    return [
      { fieldName: "state.id", value: null },
      { fieldName: "state.name", value: null },
      { fieldName: "state.stateName", value: null },
      { fieldName: "state.stateNameFr", value: null },
      { fieldName: "state.stateNameEn", value: null },
      { fieldName: "state.stateCode", value: null },
    ];
  };

  useEffect(() => {
    const data = formStateHandlers.getData() as U3148Organization | null;
    const countryId = data?.country?.id || null;
    const currentCountryId = countryId && isValidId(countryId) ? countryId : null;
    const previousCountryId = previousCountryIdRef.current;
    const countryChanged = previousCountryId !== null && previousCountryId !== currentCountryId;
    const countryCleared = previousCountryId !== null && currentCountryId === null;

    if ((countryChanged || countryCleared) && data?.state && isValidId(data.state.id)) {
      const setNestedValue = (obj: U3148Organization, path: string, value: unknown): U3148Organization => {
        const keys = path.split(".");
        const cloned = JSON.parse(JSON.stringify(obj)) as U3148Organization;
        let current: Record<string, unknown> = cloned as unknown as Record<string, unknown>;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]] as Record<string, unknown>;
        }
        current[keys[keys.length - 1]] = value;
        return cloned;
      };

      let updated = setNestedValue(data, "state.id", null);
      updated = setNestedValue(updated, "state.name", null);
      updated = setNestedValue(updated, "state.stateName", null);
      updated = setNestedValue(updated, "state.stateNameFr", null);
      updated = setNestedValue(updated, "state.stateNameEn", null);
      updated = setNestedValue(updated, "state.stateCode", null);
      formStateHandlers.setData(updated as T);
      setStateSelection.current?.(null);
      setStateInputValue.current?.("");
    }

    if (previousCountryId === null || previousCountryId !== currentCountryId) {
      previousCountryIdRef.current = currentCountryId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(formStateHandlers.getData() as U3148Organization | null)?.country?.id]);

  const countryId = (formStateHandlers.getData() as U3148Organization | null)?.country?.id;
  const stateReadonly = !countryId || countryId === "";

  return (
    <div className="grid gap-4">
      <U2000InputText
        fieldName="organizationName"
        label={t("lblOrganizationName", { ns: NS })}
        formStateHandlers={formStateHandlers}
        inlineEnd={
          <U2000CheckBox
            formStateHandlers={formStateHandlers}
            fieldName="organizationDefault"
            label={t("lblOrganizationDefault", { ns: NS })}
          />
        }
      />

      <div className="mt-3 flex items-stretch gap-4">
        <div className="flex flex-[66_66_0%] flex-col gap-4">
          <U2000Panel title={t("lblAddress", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col">
            <div className="mt-3 flex w-full flex-col gap-3">
              <U2000InputText fieldName="addressLine1" label={t("lblAddressLine1", { ns: NS })} formStateHandlers={formStateHandlers} />
              <U2000InputText fieldName="addressLine2" label={t("lblAddressLine2", { ns: NS })} formStateHandlers={formStateHandlers} />
              <div className="flex w-full gap-4">
                <div className="flex-1">
                  <U2000InputText fieldName="addressCity" label={t("lblAddressCity", { ns: NS })} formStateHandlers={formStateHandlers} />
                </div>
                <div className="flex-1">
                  <U2000InputMask
                    fieldName="addressPostalCode"
                    label={t("lblAddressPostalCode", { ns: NS })}
                    mask="postalCodeCA"
                    formStateHandlers={formStateHandlers}
                  />
                </div>
              </div>
              <div className="flex w-full gap-4">
                <div className="flex-1">
                  <U2000AutoCompleteInput
                    formStateHandlers={formStateHandlers}
                    fieldName="country"
                    mainFieldName="countryName"
                    label={t("lblAddressCountry", { ns: NS })}
                    onSearch={onSearchCountry}
                    displayFn={displayCountry}
                    overrideExtractDataFn={extractCountryData}
                    minCarLaunchSearch={0}
                    resultLabels={[]}
                    resultFields={countryResultFields}
                  />
                </div>
                <div className="flex-1">
                  <U2000AutoCompleteInput
                    formStateHandlers={formStateHandlers}
                    fieldName="state"
                    mainFieldName="stateName"
                    label={t("lblAddressState", { ns: NS })}
                    onSearch={onSearchState}
                    displayFn={displayState}
                    overrideExtractDataFn={extractStateData}
                    minCarLaunchSearch={0}
                    resultLabels={[]}
                    resultFields={stateResultFields}
                    readonly={stateReadonly}
                    onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
                      setStateSelection.current = setCurrentSelection as (item: unknown) => void;
                      setStateInputValue.current = setInputValue;
                    }}
                  />
                </div>
              </div>
            </div>
          </U2000Panel>
        </div>

        <div className="flex flex-[34_34_0%] flex-col self-stretch">
          <U3148OrganizationIconPanel formStateHandlers={formStateHandlers} t={t} canEdit={canEditIcon} />
        </div>
      </div>
    </div>
  );
}

export default U3148OrganizationBody;
